import { useEffect, useState } from 'react';
import {
  AdornedInput,
  Box,
  Button,
  ButtonGroup,
  Heading,
  Modal,
  Paragraph,
  Text,
  TextButton,
} from '@zillow/constellation-10';

const DEFAULT_ADMIN_BANNER_TIMEOUT = 5;

type AdminModalProps = {
  isImpersonator: boolean;
  onConfirm: (timeoutSeconds: number) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const AdminModal = ({ isImpersonator, onConfirm, open, onOpenChange }: AdminModalProps) => {
  const [inputValue, setInputValue] = useState<number | string>(DEFAULT_ADMIN_BANNER_TIMEOUT);

  useEffect(() => {
    if (open) {
      setInputValue(DEFAULT_ADMIN_BANNER_TIMEOUT);
    }
  }, [open]);

  const handleConfirm = () => {
    const timeoutValue = typeof inputValue === 'number' ? inputValue : DEFAULT_ADMIN_BANNER_TIMEOUT;
    onConfirm(timeoutValue);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    if (Number(newValue) > 0) {
      setInputValue(Number(newValue));
    } else if (newValue === '') {
      setInputValue(newValue);
    }
  };

  return (
    <Modal
      size="md"
      open={open}
      onOpenChange={onOpenChange}
      header={<Heading level={2}>Admin banner</Heading>}
      body={
        <Box
          css={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '400' }}
        >
          <Text textStyle="heading-xs">Banner will reappear in:</Text>

          <AdornedInput
            input={<AdornedInput.Input placeholder="5" value={inputValue} maxLength={3} onChange={handleInputChange} />}
            endAdornment={<AdornedInput.Adornment>seconds</AdornedInput.Adornment>}
            css={{ width: '3600' }}
          />

          {isImpersonator && (
            // oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: 270px` is past the end of the numeric sizes scale, which stops at `5000` (200px)
            <Paragraph textStyle="body-sm" css={{ maxWidth: '270px', color: 'text.feedback.critical.hero' }}>
              You will still be impersonating a user.
            </Paragraph>
          )}

          <Paragraph css={{ color: 'text.subtle' }}>{`Press \u2318 + Ctrl + A to toggle the admin banner.`}</Paragraph>
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Modal actions">
          <Modal.Close>
            <TextButton>Cancel</TextButton>
          </Modal.Close>
          <Modal.Close>
            <Button tone="brand" emphasis="filled" onClick={handleConfirm}>
              Confirm
            </Button>
          </Modal.Close>
        </ButtonGroup>
      }
    />
  );
};
